import { useMemo } from 'react';
import { Typography } from '@mui/material';
import { default as Grid } from '@mui/material/Unstable_Grid2';
import { useTheme } from '@mui/material/styles';

import { MetricBarChart, MetricCard, PageSection } from '@occc/ui-design-system';
import { Button } from '@occc-app/design-system';
import { RetailerNetworkTransactionMetricsOverviewFragment } from '@occc/graphql';

import { getMetrics } from './active-member-performance.utils';
import { TransactionsMetrics } from './active-member-performance.types';
import { LEGEND_LABEL } from './active-member-performance.constants';

export type ActiveMemberPerformanceProps = {
  data: RetailerNetworkTransactionMetricsOverviewFragment | undefined | null;
  isLoading?: boolean;
  hasError?: boolean;
  isViewPerformanceButtonVisible: boolean;
  onViewPerformance: () => void;
};

export const ActiveMemberPerformance = ({
  data,
  isLoading = false,
  hasError = false,
  isViewPerformanceButtonVisible,
  onViewPerformance,
}: ActiveMemberPerformanceProps) => {
  const theme = useTheme();
  const actual = data?.actualPerformance;
  const prev = data?.prevPerformance;

  const metrics: TransactionsMetrics[] = useMemo(() => {
    const series = data?.loyaltyTransactionsTimeSeries ?? [];
    return getMetrics(series);
  }, [data?.loyaltyTransactionsTimeSeries]);

  const isEmpty = !metrics.length;

  return (
    <PageSection
      title='Active Member Performance'
      description='Track member interaction in the last 30 days.'
      actions={
        isViewPerformanceButtonVisible ? (
          <Button color='primary' variant='contained' onClick={onViewPerformance}>
            View Performance
          </Button>
        ) : null
      }
      isLoading={isLoading}
      hasError={hasError}
    >
      {isEmpty ? (
        <Typography my={4} textAlign='center' variant='body2' color='text.secondary'>
          No loyalty activity reported for this period.
        </Typography>
      ) : (
        <Grid container spacing={1} p={3}>
          <Grid container xs={12} sm={6}>
            <Grid xs={6}>
              <MetricCard
                label='Loyalty Transactions Today'
                value={actual?.numberOfLoyaltyTransactionsToday ?? 0}
                prevValue={prev?.numberOfLoyaltyTransactionsToday}
                valueType='number'
                valueTypographyProps={{ variant: 'h5', fontWeight: 500 }}
              />
            </Grid>
            <Grid xs={6}>
              <MetricCard
                label='Yesterday'
                value={actual?.numberOfLoyaltyTransactionsYesterday ?? 0}
                prevValue={prev?.numberOfLoyaltyTransactionsYesterday}
                valueType='number'
                valueTypographyProps={{ variant: 'h5', fontWeight: 500 }}
              />
            </Grid>
            <Grid xs={6}>
              <MetricCard
                label='Last 7 Days'
                value={actual?.numberOfLoyaltyTransactionsLast7Days ?? 0}
                prevValue={prev?.numberOfLoyaltyTransactionsLast7Days}
                valueType='number'
                valueTypographyProps={{ variant: 'h5', fontWeight: 500 }}
              />
            </Grid>
            <Grid xs={6}>
              <MetricCard
                label='Last 30 Days'
                value={actual?.numberOfLoyaltyTransactionsLast30Days ?? 0}
                prevValue={prev?.numberOfLoyaltyTransactionsLast30Days}
                valueType='number'
                valueTypographyProps={{ variant: 'h5', fontWeight: 500 }}
              />
            </Grid>
          </Grid>

          <Grid xs={12} sm={6}>
            <Typography variant='subtitle1' ml={2} gutterBottom>
              Loyalty Transactions
            </Typography>
            <MetricBarChart
              metrics={metrics}
              barColor={theme.palette.info.main}
              barWidth={6}
              height={120}
              tickCount={5}
              isLegendVisible={false}
              isBarStacked
              legendFormatter={() => LEGEND_LABEL}
            />
          </Grid>
        </Grid>
      )}
    </PageSection>
  );
};
